import { watch } from 'vue'

/**
 * Whether a route query names exactly one task to open.
 *
 * `?task=a&task=b` parses to an array and a bare `?task` to `null` — neither
 * names one row, and neither is worth guessing at. Exported because a page may
 * need to know a link is present *before* it has any data to act on: the action
 * plan has to switch to its own tab first, since that is what starts the
 * subscription the rows arrive on.
 */
export function hasTaskDeepLink(taskId: unknown): taskId is string {
  return typeof taskId === 'string' && taskId.length > 0
}

/**
 * `?task={id}` in the URL, acted on once the page has the data to act on it —
 * MYAC-1079.
 *
 * **A watch with a latch, never a read on mount.** The rows a link names arrive
 * from Firestore *after* the view mounts, and no surface can focus something
 * that is not there yet. From a new tab — which is what these links are for —
 * the late arrival is the normal case, not an edge one, so an implementation
 * that reads `route.query` during setup works only when the data happens to be
 * warm already.
 *
 * The latch is the other half. Without it every later snapshot re-fires the
 * focus, dragging the page back to the linked row long after the user has
 * scrolled somewhere else. Once consumed, it stays consumed until the link
 * names a different task: the board owns its query (MYAC-1264), so a second
 * link followed from inside the page reaches this same instance, not a new one.
 *
 * Left as callbacks rather than taking the route and the store directly: the
 * two sides of this link — the board and the action plan — have genuinely
 * different notions of "ready" and of "open", and the part worth sharing is the
 * timing rule, not their plumbing.
 */
export function useTaskDeepLinkFocus(options: {
  /** Whether the page now holds the rows the link could name. */
  ready: () => boolean
  /** The `task` query param, unvalidated — routes hand back arrays too. */
  taskId: () => unknown
  /** Focus the row. Called at most once per task the link names. */
  open: (taskId: string) => void
}): void {
  let consumed = false

  watch(
    () => {
      const id = options.taskId()
      return [options.ready(), hasTaskDeepLink(id) ? id : null] as const
    },
    ([ready, id], previous) => {
      if (previous && id !== previous[1]) consumed = false
      if (consumed || !ready || id === null) return
      consumed = true
      options.open(id)
    },
    { immediate: true },
  )
}
